import { BooleanField, useGetOne, useRecordContext, useUpdate } from "react-admin";
import { Student } from "../providers/studentsProvider";
import { Typography } from "@mui/material";
import { BooleanInput } from "./BooleanInput";
import { DrivingSchool } from "../providers/drivingSchoolsProvider";
import { Row } from "../misc/Row";
import { Column } from "../misc/Column";
import { type DeepPartial } from "ts-essentials";

export function StudentFeatureToggles(props: { mode: "show" | "edit" }) {
  const { mode } = props;
  const student = useRecordContext<Student>();
  const { data: drivingSchool } = useGetOne<DrivingSchool>(
    "drivingSchools",
    { id: student?.drivingSchoolId },
    { enabled: !!student?.drivingSchoolId },
  );

  if (!student || !drivingSchool) {
    return null;
  }

  const theoryLearningEnabled = drivingSchool.customizations.includes("theoryLearning");

  const renderFeatureToggle = (featureToggle: keyof Student["featureToggles"], label: string) => {
    if (mode === "show") {
      return <_FeatureToggleField featureToggle={featureToggle} label={label} />;
    } else {
      return <_FeatureToggleInput featureToggle={featureToggle} label={label} />;
    }
  };

  return (
    <Column>
      {theoryLearningEnabled && renderFeatureToggle("canLearnTheoryInMobileApp", "kann Theorie in der App lernen")}
      {renderFeatureToggle("canBookTheoryLessons", "kann Theorieunterricht buchen")}
      {renderFeatureToggle("canCancelTheoryLessons", "kann Theorieunterricht absagen")}
      {renderFeatureToggle("canBookDrivingLessons", "kann Fahrstunden buchen")}
      {renderFeatureToggle("canCancelDrivingLessons", "kann Fahrstunden absagen")}
    </Column>
  );
}

function _FeatureToggleField(props: { featureToggle: keyof Student["featureToggles"]; label: string }) {
  const { featureToggle, label } = props;
  return (
    <Row>
      <BooleanField source={`featureToggles.${featureToggle}`} looseValue />
      <Typography variant="body2" className="whitespace-nowrap" sx={{ ml: "5px" }}>
        {label}
      </Typography>
    </Row>
  );
}

function _FeatureToggleInput(props: { featureToggle: keyof Student["featureToggles"]; label: string }) {
  const { featureToggle, label } = props;
  const [update, { isLoading }] = useUpdate();
  const student = useRecordContext<Student>();
  const { id } = student;

  const updateStudent = (data: DeepPartial<Student>) =>
    update(
      "students",
      { id, data, previousData: student },
      { onError: (error) => handleError("Fehler beim Speichern.", error) },
    );

  return (
    <BooleanInput
      variant="checkbox"
      source={`featureToggles.${featureToggle}`}
      label={label}
      helperText={false}
      size="small"
      sx={{
        marginLeft: "1em",
        "& .MuiCheckbox-root": { padding: "0 5px 0 0" },
      }}
      className="whitespace-nowrap"
      onChange={(event) => updateStudent({ featureToggles: { [featureToggle]: event.target.checked } })}
      disabled={isLoading}
    />
  );
}
